import type { PullRequestDemoResponse } from '@/lib/code-sessions/pull-request-demo/demo-state';
import type { ReviewSubmissionResponse } from '@/lib/github/review-submission-types';
import type { MergeabilityStatusResponse } from '@/lib/hooks/mergeability-status/query-data';
import type {
  CodeChangesResponse,
  FileContentResponse,
  PrV2OrderedFilesResponse,
  PrV2RepoFileQuery,
  PrV2RepoFileResult,
} from '@/lib/pr-v2/code-changes/types';
import type { PrV2FileDescriptionResponse } from '@/lib/pr-v2/file-review-decisions';
import type {
  CreatePrV2CommentInput,
  PrV2AiAttributionResponse,
  PrV2AvailableLabel,
  PrV2AvailableLabelsResponse,
  PrV2CheckRunResponse,
  PrV2CommentResponse,
  PrV2CommentType,
  PrV2CubicReviewSummaryResponse,
  PrV2DiscussionResponse,
  PrV2LabelsResponse,
  PrV2MergeabilityCapabilitiesResponse,
  PrV2MergeMethod,
  PrV2MergeRequest,
  PrV2MergeResponse,
  PrV2ReactionContent,
  PrV2ReactionSubjectType,
  PrV2ReviewResponse,
  PrV2ReviewThread,
  PrV2StackResponse,
  PrV2SubmitReviewInput,
  PrV2SummaryResponse,
  UpdatePrV2CommentInput,
  UpdatePrV2LabelsInput,
} from '@/lib/pr-v2/types';

import type { PrV2Version, UpdatePrV2StateInput } from './types';

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: string } | null;
    const error = new Error(body?.error ?? `Request failed: ${response.status}`);
    (error as Error & { status?: number }).status = response.status;
    throw error;
  }

  return response.json() as Promise<T>;
}

/**
 * Whether a PR v2 read failed because the viewer may not make it: signed out, not allowed, or a
 * pull request hidden from them. Reads the status `fetchJson` attaches to its errors.
 */
export function isPrV2AccessError(error: unknown): boolean {
  if (!error || typeof error !== 'object' || !('status' in error)) {
    return false;
  }
  return error.status === 401 || error.status === 403 || error.status === 404;
}

export async function fetchPrV2Summary(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2SummaryResponse> {
  return fetchJson<PrV2SummaryResponse>(`/api/v2/pull-requests/${owner}/${repo}/${number}`, {
    signal,
  });
}

export async function fetchPrV2AiAttribution(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2AiAttributionResponse> {
  return fetchJson<PrV2AiAttributionResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/ai-attribution`,
    {
      signal,
    },
  );
}

export async function fetchPrV2Demo(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PullRequestDemoResponse> {
  return fetchJson<PullRequestDemoResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/demo`,
    { signal },
  );
}

export async function rerecordPrV2Demo(
  owner: string,
  repo: string,
  number: string,
): Promise<PullRequestDemoResponse> {
  return fetchJson<PullRequestDemoResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/demo`,
    { method: 'POST' },
  );
}

export async function fetchPrV2Stack(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2StackResponse> {
  return fetchJson<PrV2StackResponse>(`/api/v2/pull-requests/${owner}/${repo}/${number}/stack`, {
    signal,
  });
}

export async function fetchPrV2Versions(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2Version[]> {
  const response = await fetchJson<{ versions: PrV2Version[] }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/versions`,
    { signal },
  );

  return response.versions;
}

export async function updatePrV2Title(
  owner: string,
  repo: string,
  number: string,
  title: string,
): Promise<{ title: string }> {
  return fetchJson<{ title: string }>(`/api/v2/pull-requests/${owner}/${repo}/${number}/title`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title }),
  });
}

export async function updatePrV2Description(
  owner: string,
  repo: string,
  number: string,
  body: string,
): Promise<{ body: string }> {
  return fetchJson<{ body: string }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/description`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ body }),
    },
  );
}

export async function updatePrV2State(
  owner: string,
  repo: string,
  number: string,
  payload: UpdatePrV2StateInput,
): Promise<{ state?: 'open' | 'closed'; draft?: boolean }> {
  return fetchJson<{ state?: 'open' | 'closed'; draft?: boolean }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/state`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function updatePrV2Labels(
  owner: string,
  repo: string,
  number: string,
  changes: UpdatePrV2LabelsInput,
): Promise<PrV2LabelsResponse> {
  return fetchJson<PrV2LabelsResponse>(`/api/v2/pull-requests/${owner}/${repo}/${number}/labels`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(changes),
  });
}

export async function fetchPrV2AvailableLabels(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2AvailableLabel[]> {
  const response = await fetchJson<PrV2AvailableLabelsResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/available-labels`,
    { signal },
  );

  return response.labels;
}

export async function fetchPrV2Discussion(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2DiscussionResponse> {
  return fetchJson<PrV2DiscussionResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/discussion`,
    { signal },
  );
}

export async function fetchPrV2MergeabilityStatus(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<MergeabilityStatusResponse> {
  return fetchJson<MergeabilityStatusResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/mergeability`,
    { signal },
  );
}

export async function fetchPrV2MergeabilityCapabilities(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2MergeabilityCapabilitiesResponse> {
  return fetchJson<PrV2MergeabilityCapabilitiesResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/mergeability/capabilities`,
    { signal },
  );
}

export async function fetchPrV2Reviews(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2ReviewResponse[]> {
  const response = await fetchJson<{ reviews: PrV2ReviewResponse[] }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/reviews`,
    { signal },
  );

  return response.reviews;
}

export async function fetchPrV2Comments(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2CommentResponse[]> {
  const response = await fetchJson<{ comments: PrV2CommentResponse[] }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments`,
    { signal },
  );

  return response.comments;
}

export async function fetchPrV2Checks(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2CheckRunResponse[]> {
  const response = await fetchJson<{ checks: PrV2CheckRunResponse[] }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/checks`,
    { signal },
  );

  return response.checks;
}

export async function fetchPrV2CubicReviewSummary(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2CubicReviewSummaryResponse> {
  return fetchJson<PrV2CubicReviewSummaryResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/cubic-comments`,
    { signal },
  );
}

export async function fetchPrV2ReviewThreads(
  owner: string,
  repo: string,
  number: string,
  signal?: AbortSignal,
): Promise<PrV2ReviewThread[]> {
  const response = await fetchJson<{ threads: PrV2ReviewThread[] }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/review-threads`,
    { signal },
  );

  return response.threads;
}

export async function postPrV2Comment(
  owner: string,
  repo: string,
  number: string,
  payload: CreatePrV2CommentInput,
): Promise<unknown> {
  return fetchJson(`/api/v2/pull-requests/${owner}/${repo}/${number}/comments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function replyToPrV2Comment(
  owner: string,
  repo: string,
  number: string,
  commentId: number,
  body: string,
): Promise<unknown> {
  return fetchJson(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments/${commentId}/replies`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ body }),
    },
  );
}

export async function createPrV2CommentReaction(
  owner: string,
  repo: string,
  number: string,
  commentId: number,
  payload: {
    commentType: PrV2ReactionSubjectType;
    content: PrV2ReactionContent;
  },
): Promise<{ success: boolean; reactionId: number | null }> {
  return fetchJson<{ success: boolean; reactionId: number | null }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments/${commentId}/reactions`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function deletePrV2CommentReaction(
  owner: string,
  repo: string,
  number: string,
  commentId: number,
  reactionId: number,
  commentType: PrV2ReactionSubjectType,
  content?: PrV2ReactionContent,
): Promise<{ success: boolean }> {
  const query = new URLSearchParams(
    content
      ? {
          commentType,
          content,
        }
      : {
          commentType,
        },
  );
  return fetchJson<{ success: boolean }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments/${commentId}/reactions/${reactionId}?${query.toString()}`,
    {
      method: 'DELETE',
    },
  );
}

export async function resolvePrV2CommentThread(
  owner: string,
  repo: string,
  number: string,
  commentId: number,
  resolve: boolean,
  threadNodeId: string,
): Promise<unknown> {
  return fetchJson(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments/${commentId}/resolve`,
    {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        resolve,
        threadNodeId,
      }),
    },
  );
}

export async function editPrV2Comment(
  owner: string,
  repo: string,
  number: string,
  payload: UpdatePrV2CommentInput,
): Promise<unknown> {
  return fetchJson(`/api/v2/pull-requests/${owner}/${repo}/${number}/comments`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function deletePrV2Comment(
  owner: string,
  repo: string,
  number: string,
  commentId: number,
  commentType: PrV2CommentType,
): Promise<{ success: boolean }> {
  return fetchJson<{ success: boolean }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/comments?commentId=${commentId}&commentType=${commentType}`,
    {
      method: 'DELETE',
    },
  );
}

export async function updatePrV2Reviewers(
  owner: string,
  repo: string,
  number: string,
  payload: {
    reviewers: string[];
    teamReviewers: string[];
    method: 'POST' | 'DELETE';
  },
): Promise<unknown> {
  return fetchJson(`/api/v2/pull-requests/${owner}/${repo}/${number}/reviewers`, {
    method: payload.method,
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      reviewers: payload.reviewers,
      team_reviewers: payload.teamReviewers,
    }),
  });
}

export async function dismissPrV2Review(
  owner: string,
  repo: string,
  number: string,
  reviewId: number,
  message: string,
): Promise<unknown> {
  return fetchJson(`/api/v2/pull-requests/${owner}/${repo}/${number}/reviews/${reviewId}/dismiss`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });
}

export async function refreshPrV2(
  owner: string,
  repo: string,
  number: string,
): Promise<{ status: string; deliveryId: string }> {
  return fetchJson<{ status: string; deliveryId: string }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/refresh`,
    {
      method: 'POST',
    },
  );
}

export async function submitPrV2Review(
  owner: string,
  repo: string,
  number: string,
  payload: PrV2SubmitReviewInput,
): Promise<ReviewSubmissionResponse> {
  return fetchJson<ReviewSubmissionResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/review`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function mergePrV2(
  owner: string,
  repo: string,
  number: string,
  payload: PrV2MergeRequest,
): Promise<PrV2MergeResponse> {
  return fetchJson<PrV2MergeResponse>(`/api/v2/pull-requests/${owner}/${repo}/${number}/merge`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
}

export async function setPrV2AutoMerge(
  owner: string,
  repo: string,
  number: string,
  payload: {
    pullRequestId: string;
    mergeMethod: PrV2MergeMethod;
    action: 'enable' | 'disable';
  },
): Promise<{ success: boolean; data?: unknown }> {
  return fetchJson<{ success: boolean; data?: unknown }>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/auto-merge`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );
}

export async function fetchPrV2CodeChanges(
  owner: string,
  repo: string,
  number: string,
  options?: {
    headSha?: string | null;
    baseSha?: string | null;
  },
  signal?: AbortSignal,
): Promise<CodeChangesResponse> {
  const params = new URLSearchParams();

  if (options?.headSha) {
    params.set('headSha', options.headSha);
  }

  if (options?.baseSha) {
    params.set('baseSha', options.baseSha);
  }

  const query = params.toString();
  const url = query
    ? `/api/v2/pull-requests/${owner}/${repo}/${number}/code-changes?${query}`
    : `/api/v2/pull-requests/${owner}/${repo}/${number}/code-changes`;

  return fetchJson<CodeChangesResponse>(url, { signal });
}

export async function fetchPrV2OrderedFiles(
  owner: string,
  repo: string,
  number: string,
  options?: {
    headSha?: string | null;
    baseSha?: string | null;
  },
  signal?: AbortSignal,
): Promise<PrV2OrderedFilesResponse> {
  const params = new URLSearchParams();

  if (options?.headSha) {
    params.set('headSha', options.headSha);
  }

  if (options?.baseSha) {
    params.set('baseSha', options.baseSha);
  }

  const query = params.toString();
  const url = query
    ? `/api/v2/pull-requests/${owner}/${repo}/${number}/ordered-files?${query}`
    : `/api/v2/pull-requests/${owner}/${repo}/${number}/ordered-files`;

  return fetchJson<PrV2OrderedFilesResponse>(url, { signal });
}

export async function fetchPrV2FileContent(
  owner: string,
  repo: string,
  number: string,
  options: {
    filename: string;
    previousFilename?: string | null;
    sha: string;
    status: string;
    headSha?: string | null;
    baseSha?: string | null;
  },
  signal?: AbortSignal,
): Promise<FileContentResponse> {
  const params = new URLSearchParams({
    filename: options.filename,
    sha: options.sha,
    status: options.status,
  });

  // Old-side path for renamed files so the server fetches the pre-rename blob.
  if (options.previousFilename) {
    params.set('previousFilename', options.previousFilename);
  }

  if (options.headSha) {
    params.set('headSha', options.headSha);
  }

  if (options.baseSha) {
    params.set('baseSha', options.baseSha);
  }

  return fetchJson<FileContentResponse>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/code-changes/file?${params.toString()}`,
    { signal },
  );
}

export async function fetchPrV2RepoFile(
  owner: string,
  repo: string,
  number: string,
  query: PrV2RepoFileQuery,
  signal?: AbortSignal,
): Promise<PrV2RepoFileResult> {
  const params = new URLSearchParams({
    specifier: query.specifier,
    fromPath: query.fromPath,
    side: query.side,
    headSha: query.headSha,
    baseSha: query.baseSha,
  });

  return fetchJson<PrV2RepoFileResult>(
    `/api/v2/pull-requests/${owner}/${repo}/${number}/code-changes/repo-file?${params.toString()}`,
    { signal },
  );
}

export async function fetchPrV2FileDescription(
  owner: string,
  repo: string,
  number: string,
  options?: {
    headSha?: string | null;
    baseSha?: string | null;
  },
  signal?: AbortSignal,
): Promise<PrV2FileDescriptionResponse> {
  const params = new URLSearchParams();

  if (options?.headSha) {
    params.set('headSha', options.headSha);
  }

  if (options?.baseSha) {
    params.set('baseSha', options.baseSha);
  }

  const query = params.toString();
  const url = query
    ? `/api/v2/pull-requests/${owner}/${repo}/${number}/file-description?${query}`
    : `/api/v2/pull-requests/${owner}/${repo}/${number}/file-description`;

  return fetchJson<PrV2FileDescriptionResponse>(url, { signal });
}
